// Единственная точка входа продуктовой аналитики витрины.
//
// Аналитики в IEC ID **сегодня нет**: счётчик не загружается, события никуда не
// уходят. Модуль всё равно заведён, и это не задел «на будущее», а гейт: когда
// централизованная аналитика экосистемы появится, её инициализация обязана
// пройти через `startAnalytics()` — то есть через проверку согласия. Если бы
// точки входа не было, первый же `posthog.init()` в `main.tsx` сделал бы
// тумблер в баннере декорацией.
//
// Правило одно: **никакой сетевой активности до согласия**. Отсутствие ответа
// на баннер = отказ (`isCookieCategoryGranted` отдаёт `false`), а не «пока
// можно» (GDPR ст. 4(11)).
import { isCookieCategoryGranted, useCookieConsent } from "@iec-id/ui";
import { useEffect } from "react";

type AnalyticsEvent = {
	readonly name: string;
	readonly properties?: Record<string, unknown> | undefined;
};

/** Загружен ли провайдер. Пока провайдера нет — всегда `false`. */
let running = false;

/**
 * Поднять аналитику. Вызывается ТОЛЬКО отсюда и только при живом согласии.
 *
 * Здесь появится единственный `init` провайдера экосистемы. До тех пор —
 * честная запись в консоль разработчика: молчаливый no-op читался бы как
 * «аналитика работает».
 */
export function startAnalytics(): void {
	if (running) return;
	if (!isCookieCategoryGranted("analytics")) return;
	running = true;

	if (import.meta.env.DEV) {
		// Не structured logging: на клиенте Pino нет, а в прод-бандл ветка не
		// попадает (`import.meta.env.DEV` вырезается сборкой).
		console.info(
			"[analytics] согласие есть, но провайдер аналитики ещё не подключён — события не отправляются",
		);
	}
}

/**
 * Погасить аналитику и стереть её следы. Вызывается при отзыве согласия:
 * «перестали слать события» — это половина отзыва, вторая половина — убрать
 * то, что провайдер уже положил в браузер.
 */
export function stopAnalytics(): void {
	running = false;
	// Здесь будет `posthog.reset()` + удаление cookie провайдера.
}

/**
 * Отправить событие. Без согласия — тихий no-op: вызывающему коду не нужно
 * помнить про согласие, за него отвечает этот модуль.
 *
 * Потребителей у неё пока нет, и это не мёртвый код: провайдера аналитики в
 * IEC ID ещё не подключили, а функция — ГЕЙТ согласия. Удалить её значит
 * пригласить следующего позвать `posthog.capture` напрямую, мимо проверки.
 *
 * @public
 */
export function trackEvent(event: AnalyticsEvent): void {
	if (!running) return;
	if (!isCookieCategoryGranted("analytics")) {
		// Согласие отозвано между стартом и вызовом — гасим на месте.
		stopAnalytics();
		return;
	}
	// Здесь будет `posthog.capture(event.name, event.properties)`.
	void event;
}

/**
 * Держит аналитику в соответствии с текущим согласием. Монтируется один раз
 * в корне приложения: согласие меняется и из баннера, и из витрины, и из
 * соседней вкладки — реагировать обязаны все три случая.
 */
export function useAnalyticsConsent(): void {
	const consent = useCookieConsent();
	const granted = consent?.choices.analytics === true;

	useEffect(() => {
		if (granted) startAnalytics();
		else stopAnalytics();
	}, [granted]);
}
